import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Chip,
  DataList,
  EmptyState,
  ErrorState,
  Nota,
  PageHeader,
  Pagination,
  SearchInput,
  Skeleton,
  Tabs,
  idDaAba,
  idDoPainel,
} from '../../../ui';

import ModalConfirmacao from '../../../components/modal/confirmacao/ModalConfirmacao';
import ModalInformacoesEnviadas from '../../../components/modal/informacoes-enviadas/ModalInformacoesEnviadas';
import ModalFiltrosEnvios, {
  FILTROS_VAZIOS,
  FiltrosEnvios,
  contarFiltrosAtivos,
} from '../../../components/modal/filtros-envios/ModalFiltrosEnvios';
import ModalDetalhesEnvioAssociado from '../../../components/modal/detalhes-envios/ModalDetalhesEnvioAssociado';
import ModalDetalhesEnvioVistoria from '../../../components/modal/detalhes-envios/ModalDetalhesEnvioVistoria';
import ModalDetalhesDivergencia from '../../../components/modal/detalhes-envios/ModalDetalhesDivergencia';
import AvisoDivergenciaSga from '../../../components/divergencia-sga/AvisoDivergenciaSga';

import { TI } from '../routes';
import {
  AssociadoEnvioSga,
  CarroEnvioSga,
  EnviosSgaResponse,
  fetchEnviosSga,
} from '../../../api/associado/associadoApi';
import { enviarAssociadoParaSgaPorCpfCnpj } from '../../../api/sga-associado/sga-associadoApi';
import { enviarVeiculoParaSgaPorPlaca } from '../../../api/sga-veiculo/sga-veiculoApi';
import { reconciliarAssociado } from '../../../api/sga-reconciliacao/sgaReconciliacaoApi';
import { interpretarVerificacaoSga } from '../../../utils/verificacaoSga';
import {
  AcompanhamentoVistoriasResponse,
  VistoriaEnvioSga,
  enviarFotosVistoriaPorPlaca,
  enviarVistoriaPorId,
  fetchAcompanhamentoVistorias,
} from '../../../api/sga-vistoria/sga-vistoriaApi';
import {
  DivergenciaFila,
  DivergenciaSga,
  ListaDivergencias,
  aceitarDivergencia,
  ignorarDivergencia,
  listarDivergencias,
} from '../../../api/sga-divergencia/sgaDivergenciaApi';

import { extrairMensagemErro } from '../../../utils/apiError';
import { formatCpfCnpj } from '../../../utils/formartCPF';
import { formatarDataCurta } from '../../../utils/data';
/* 📌 O rótulo "Motocicleta elétrica" NÃO aparece nesta lista, e sim no modal de
   detalhes — que é onde os veículos passaram a viver (D9). A linha aqui é do
   ASSOCIADO; a identificação `BIK0001` nem chega a ser exibida nela. */
import { rotuloEnvioSga } from '../../../utils/vistoriaFluxo';
import {
  NOTA_ENVIO_AUTOMATICO,
  rotuloEnvioRegistro,
  rotuloOrigemDivergencia,
  rotuloStatusDivergencia,
  rotuloTipoRegistro,
  valorOuTraco,
} from '../../../utils/envioSgaRegras';

/**
 * `/ti/envios` — **Acompanhamento de Envios ao SGA** (at-06/at-13/at-28/at-32;
 * reescrita na at-70, frente D/D8).
 *
 * Visão global de TI/ADMIN da fila de envios, em três abas: associados e veículos,
 * vistorias, e a fila de divergências local × SGA. Cada linha traz o estado e as
 * ações de envio; o histórico do que foi transmitido fica em "Ver envio".
 *
 * ## Por que ela era a última tela legada do hub
 *
 * Ela recebeu, antes de o design system existir, os padrões que o kit depois
 * formalizou — cards no celular, modal de detalhes, filtros em modal, roving
 * tabindex — **todos escritos à mão**. O que esta reescrita desfaz é duplicação:
 * abas próprias (40 linhas), debounce próprio, três `Math.ceil`, tabela **e** lista
 * de cards no mesmo arquivo, quatro overlays de modal e 17 informações em `title`.
 *
 * ## O que mudou de comportamento
 *
 *   1. 🔴 **As bolinhas viraram chips com texto.** Cinco colunas marcavam estado com
 *      um `<span>` de 10px verde ou vermelho, cujo único texto vivia num `title` —
 *      invisível no celular e para o teclado. É o que a spec 02 §9 e a decisão de
 *      Victor da at-54 proíbem. E **pendência agora é âmbar**: "ainda não foi ao
 *      SGA" não é erro, o cron reenvia sozinho.
 *   2. 🔴 **Uma linha por ASSOCIADO, com os veículos no modal — no desktop também.**
 *      A tabela misturava duas entidades (o titular, tingido de laranja, e N
 *      sub-linhas de veículo). É o desenho que o celular já usava desde 03/09/2026
 *      e o mesmo princípio da carteira (Victor, 31/08/2026: *"muita informação num
 *      lugar só"*). "Enviar associado" e "Ver envio" ficam na linha, porque são do
 *      titular.
 *   3. 🐛 **Falha de carga era muda** (um `console`/`toast` e a tabela com "Nenhum
 *      associado encontrado", que **fica**). Agora é `ErrorState` com "Tentar
 *      novamente", e a lista anterior é **descartada** — dado velho passando por
 *      atual é o defeito que a at-66 nomeou.
 *   4. **O `FullScreenLoader` saiu.** Ele cobria a tela inteira em toda carga **e em
 *      toda ação de envio**: quem clicava "Enviar veículo" perdia de vista a linha
 *      em que clicou. Agora o carregamento é `Skeleton` no painel e `carregando` no
 *      botão **daquela** linha.
 *   5. **O aviso dos totais saiu do `title`** e virou texto; os vazios distinguem
 *      "não há" de "a busca não achou" e de "o filtro não casou".
 *   6. 🔴 **O `ModalConfirmacao` não abre mais POR CIMA do modal de detalhes.** Ver
 *      `resolver()`.
 *
 * ⚠️ **Nada de negócio mudou**: mesmas rotas, mesmos parâmetros, `LIMIT = 10`, os
 * mesmos textos de confirmação e de toast, as mesmas travas ("Enviar veículo" exige
 * o associado no SGA; "Enviar fotos" exige `codigoVeiculo`), e trocar de aba zera os
 * filtros que não existem nela (at-28).
 */

const LIMIT = 10;

type Aba = 'associados' | 'vistorias' | 'divergencias';
type EstadoLista = 'carregando' | 'ok' | 'erro';

const ABAS: { id: Aba; rotulo: string }[] = [
  { id: 'associados', rotulo: 'Associados / Veículos' },
  { id: 'vistorias', rotulo: 'Vistorias' },
  { id: 'divergencias', rotulo: 'Divergências' },
];

const PLACEHOLDER_BUSCA: Record<Aba, string> = {
  associados: 'Buscar por nome do associado ou placa',
  vistorias: 'Buscar por placa ou nome do associado',
  divergencias: 'Buscar por nome do associado, placa ou CPF',
};

const EnviosSgaPage: React.FC = () => {
  const voltar = useVoltar(TI());

  const [aba, setAba] = useState<Aba>('associados');
  const [page, setPage] = useState(1);
  const [busca, setBusca] = useState('');
  const [chaveBusca, setChaveBusca] = useState(0);
  const [filtros, setFiltros] = useState<FiltrosEnvios>(FILTROS_VAZIOS);
  const [filtrosAberto, setFiltrosAberto] = useState(false);

  const [estado, setEstado] = useState<EstadoLista>('carregando');
  const [erro, setErro] = useState('');

  const [dados, setDados] = useState<EnviosSgaResponse | null>(null);
  const [dadosVistorias, setDadosVistorias] = useState<AcompanhamentoVistoriasResponse | null>(null);
  const [dadosDivergencias, setDadosDivergencias] = useState<ListaDivergencias | null>(null);

  /** Chave da ação em curso: "associado:<id>", "carro:<id>", "vistoria:<id>", "fotos:<id>". */
  const [enviando, setEnviando] = useState<string | null>(null);

  const [infoEnviadas, setInfoEnviadas] = useState<{
    filtro: { cpfCnpj?: string; placa?: string };
    titulo: string;
  } | null>(null);

  /**
   * Modais de detalhe. Guardam só o ID: o objeto é derivado da lista carregada,
   * então uma ação feita dentro do modal (que recarrega a página) atualiza o que
   * está aberto, em vez de deixar na tela um botão "Enviar" de um registro que já
   * subiu.
   */
  const [detalheAssociadoId, setDetalheAssociadoId] = useState<string | null>(null);
  const [detalheVistoriaId, setDetalheVistoriaId] = useState<string | null>(null);
  const [detalheDivergenciaId, setDetalheDivergenciaId] = useState<string | null>(null);

  const [resolucao, setResolucao] = useState<{
    divergencia: DivergenciaFila;
    acao: 'aceitar' | 'ignorar';
  } | null>(null);
  const [resolvendo, setResolvendo] = useState(false);

  /**
   * "Verificar no SGA" (at-76, pedido de Victor em 15/09/2026): o TI passa a
   * conferir um cadastro contra o SGA sem depender de pedir ao consultor.
   *
   * ⚠️ **Nenhuma mudança de backend foi necessária:** as rotas de
   * `/sga-reconciliacao` são autenticadas e o escopo é por carteira — TI/ADMIN já
   * alcança qualquer uma (`exigirCarteira` + `ELEVATED_USER_TYPES`). O que faltava
   * era o caminho na tela.
   */
  const [verificandoSgaId, setVerificandoSgaId] = useState<string | null>(null);
  const [divergenciasSga, setDivergenciasSga] = useState<DivergenciaSga[]>([]);
  const [associadoVerificadoId, setAssociadoVerificadoId] = useState<string | null>(null);

  const { situacao, tipo, statusDivergencia, entidadeDivergencia } = filtros;
  const filtrosAtivos = contarFiltrosAtivos(filtros);
  const ocupado = estado === 'carregando' || enviando !== null;

  const detalheAssociado = dados?.items.find((a) => a.id === detalheAssociadoId) ?? null;
  const detalheVistoria = dadosVistorias?.items.find((v) => v.id === detalheVistoriaId) ?? null;
  const detalheDivergencia =
    dadosDivergencias?.items.find((d) => d.id === detalheDivergenciaId) ?? null;

  const carregar = useCallback(async () => {
    setEstado('carregando');
    try {
      if (aba === 'divergencias') {
        setDadosDivergencias(
          await listarDivergencias({
            page,
            limit: LIMIT,
            busca: busca || undefined,
            status: statusDivergencia,
            entidade: entidadeDivergencia || undefined,
          }),
        );
      } else if (aba === 'associados') {
        setDados(await fetchEnviosSga(page, LIMIT, busca, situacao, tipo || undefined));
      } else {
        // o filtro de tipo é do veículo — não existe na aba de vistorias
        setDadosVistorias(await fetchAcompanhamentoVistorias(page, LIMIT, busca, situacao));
      }
      setErro('');
      setEstado('ok');
    } catch (falha) {
      // Lista DESCARTADA: manter a anterior faria dado velho passar por atual — e
      // aqui o TI decide o que enviar ao SGA a partir do que vê (lição da at-66).
      if (aba === 'divergencias') setDadosDivergencias(null);
      else if (aba === 'associados') setDados(null);
      else setDadosVistorias(null);

      setErro(extrairMensagemErro(falha, ''));
      setEstado('erro');
    }
  }, [aba, page, busca, situacao, tipo, statusDivergencia, entidadeDivergencia]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /**
   * Troca de aba. Os filtros que não existem na aba de destino são zerados —
   * mantê-los faria o contador do botão "Filtros" mentir sobre o que está ativo
   * (at-28).
   */
  const trocarAba = (nova: Aba) => {
    if (nova === aba) return;
    if (nova === 'vistorias') setFiltros((f) => ({ ...f, tipo: '' }));
    else if (nova === 'divergencias') setFiltros((f) => ({ ...f, situacao: 'TODOS', tipo: '' }));
    setPage(1);
    setAba(nova);
  };

  const trocarBusca = (termo: string) => {
    setBusca(termo);
    setPage(1);
  };

  const limparBusca = () => {
    setBusca('');
    setPage(1);
    setChaveBusca((c) => c + 1);
  };

  const limparFiltros = () => {
    setFiltros(FILTROS_VAZIOS);
    setPage(1);
  };

  /** Toda ação de envio segue o mesmo roteiro: trava a linha, avisa, recarrega. */
  const executarEnvio = async (chave: string, acao: () => Promise<unknown>, sucesso: string) => {
    setEnviando(chave);
    try {
      await acao();
      toast.success(sucesso);
      await carregar();
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Não foi possível enviar ao SGA.'));
    } finally {
      setEnviando(null);
    }
  };

  const enviarAssociado = (associado: AssociadoEnvioSga) =>
    executarEnvio(
      `associado:${associado.id}`,
      () => enviarAssociadoParaSgaPorCpfCnpj(associado.cpfCnpj),
      `Associado ${associado.nomeCompleto} enviado ao SGA`,
    );

  const enviarVeiculo = (placa: string, carroId: string) =>
    executarEnvio(
      `carro:${carroId}`,
      () => enviarVeiculoParaSgaPorPlaca(placa),
      `Veículo ${placa} enviado ao SGA`,
    );

  const enviarVistoria = (v: VistoriaEnvioSga) =>
    executarEnvio(
      `vistoria:${v.id}`,
      () => enviarVistoriaPorId(v.id),
      `Vistoria da placa ${v.placa} enviada ao SGA`,
    );

  const enviarFotos = (v: VistoriaEnvioSga) =>
    executarEnvio(
      `fotos:${v.id}`,
      () => enviarFotosVistoriaPorPlaca(v.placa),
      `Fotos da placa ${v.placa} enviadas ao SGA`,
    );

  /**
   * "Ver o que foi enviado" fecha o modal de detalhes antes de abrir o histórico.
   *
   * ⚠️ Empilhar duas camadas de diálogo deixa sem saída óbvia de volta no celular —
   * e com o `Modal` do kit ficaria pior, porque a trava de Tab e o Esc são globais.
   * Chamado também pelas linhas, onde limpar os ids é no-op.
   */
  const abrirInformacoesEnviadas = (
    filtro: { cpfCnpj?: string; placa?: string },
    titulo: string,
  ) => {
    setDetalheAssociadoId(null);
    setDetalheVistoriaId(null);
    setInfoEnviadas({ filtro, titulo });
  };

  /**
   * Confere o cadastro contra o SGA, sob demanda (at-76).
   *
   * 🔴 **Recarrega a lista quando algo é encontrado no SGA**, e é isso que atende
   * ao pedido de origem: se a placa estava lá e o registro local não tinha o
   * código, o backend grava o vínculo — e sem o recarregamento o veículo
   * continuaria aparecendo como "envio pendente" numa tela que acabou de
   * descobrir o contrário.
   *
   * ⚠️ Uma chamada por clique, nunca no carregamento da lista: seriam 10+ idas ao
   * SGA por página (mesma razão da at-32 em `/adesoes`).
   */
  const verificarNoSga = async (a: AssociadoEnvioSga) => {
    if (!a.id) return;

    setVerificandoSgaId(a.id);
    setAssociadoVerificadoId(a.id);
    setDivergenciasSga([]);
    try {
      // Nunca lança: SGA fora do ar volta com verificado=false e a tela segue.
      const leitura = interpretarVerificacaoSga(await reconciliarAssociado(a.id));

      if (leitura.situacao === 'indisponivel') {
        toast.info(leitura.mensagem);
        return;
      }

      if (leitura.situacao === 'sem-diferencas') {
        toast.success(leitura.mensagem);
      } else if (leitura.situacao === 'veiculo-vinculado') {
        // at-78: o veículo foi achado no SGA (por placa ou pelo chassi) e vinculado.
        // A lista muda logo abaixo, no `carregar()` — o toast diz o porquê.
        toast.success(leitura.mensagem, { autoClose: 8000 });
        setDivergenciasSga(leitura.divergencias);
      } else if (leitura.situacao === 'veiculo-pendente') {
        /*
         * at-78 (B23): o associado está conferido, mas o veículo NÃO foi
         * encontrado no SGA. Antes isto caía em "nenhuma diferença" e o TI
         * concluía que a pendência devia ter sumido.
         *
         * ⚠️ `toast.info`, não `success`: não é erro (o cadastro do associado está
         * certo), mas também não é "tudo resolvido".
         */
        toast.info(leitura.mensagem, { autoClose: 8000 });
      } else {
        setDivergenciasSga(leitura.divergencias);
        if (leitura.reexibidas > 0) toast.info(leitura.mensagem, { autoClose: 6000 });
      }

      // O vínculo pode ter mudado o estado de envio deste registro.
      await carregar();
    } finally {
      setVerificandoSgaId(null);
    }
  };

  const verEnvioAssociado = (a: AssociadoEnvioSga) =>
    abrirInformacoesEnviadas({ cpfCnpj: a.cpfCnpj }, `Associado: ${a.nomeCompleto}`);
  const verEnvioVeiculo = (c: CarroEnvioSga) =>
    abrirInformacoesEnviadas({ placa: c.placa }, `Veículo: ${c.placa}`);
  const verEnvioVistoria = (v: VistoriaEnvioSga) =>
    abrirInformacoesEnviadas({ placa: v.placa }, `Vistoria: ${v.placa}`);

  /**
   * 🔴 Abre a confirmação **fechando antes o modal de detalhes**, quando houver.
   *
   * O modal legado tinha uma prop (`confirmacaoAberta`) só para o Esc não fechar os
   * dois de uma vez. Com o `Modal` do kit isso não bastaria: o listener de Esc e a
   * trava de Tab são globais, então o Tab escaparia da confirmação para a página.
   * E não há o que preservar atrás: depois de resolver, a divergência sai da fila.
   */
  const resolver = (divergencia: DivergenciaFila, acao: 'aceitar' | 'ignorar') => {
    setDetalheDivergenciaId(null);
    setResolucao({ divergencia, acao });
  };

  const confirmarResolucao = async () => {
    if (!resolucao) return;

    const { divergencia, acao } = resolucao;
    setResolvendo(true);
    try {
      const resultado =
        acao === 'aceitar'
          ? await aceitarDivergencia(divergencia.id)
          : await ignorarDivergencia(divergencia.id);

      if (acao === 'ignorar') {
        toast.success('Divergência de ' + resultado.rotulo + ' ignorada.');
      } else if (resultado.aplicado) {
        toast.success(resultado.rotulo + ' atualizado com o dado do SGA.');
      } else {
        // Campo sem aplicação automática (troca de titular, veículo substituído,
        // campo único): sai da fila, mas a ação de verdade é manual.
        toast.info(
          resultado.orientacao ??
            'Divergência marcada como resolvida — este campo exige ajuste manual.',
        );
      }

      setResolucao(null);
      await carregar();
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Não foi possível resolver a divergência.'));
    } finally {
      setResolvendo(false);
    }
  };

  /* ───────────────────────── colunas ───────────────────────── */

  const colunasAssociados = [
    {
      id: 'associado',
      rotulo: 'Associado',
      principal: true,
      celula: (a: AssociadoEnvioSga) => a.nomeCompleto,
    },
    {
      id: 'cpf',
      rotulo: 'CPF / CNPJ',
      secundaria: true,
      celula: (a: AssociadoEnvioSga) => formatCpfCnpj(a.cpfCnpj),
    },
    {
      id: 'situacao',
      rotulo: 'Situação',
      celula: (a: AssociadoEnvioSga) => <SituacaoDoAssociado associado={a} />,
    },
    {
      id: 'tipos',
      rotulo: 'Tipo',
      celula: (a: AssociadoEnvioSga) => <TiposDosVeiculos associado={a} />,
    },
    {
      id: 'consultor',
      rotulo: 'Consultor',
      ocultarNoCard: true,
      celula: (a: AssociadoEnvioSga) => a.consultor || '—',
    },
    {
      id: 'cadastro',
      rotulo: 'Cadastrado em',
      ocultarNoCard: true,
      celula: (a: AssociadoEnvioSga) => formatarDataCurta(a.createdAt),
    },
  ];

  const colunasVistorias = [
    {
      id: 'placa',
      rotulo: 'Veículo',
      principal: true,
      celula: (v: VistoriaEnvioSga) => v.placa,
    },
    {
      id: 'modelo',
      rotulo: 'Modelo',
      secundaria: true,
      celula: (v: VistoriaEnvioSga) => v.modelo || '—',
    },
    {
      id: 'associado',
      rotulo: 'Associado',
      celula: (v: VistoriaEnvioSga) => v.associado || '—',
    },
    {
      id: 'envio',
      rotulo: 'Envio ao SGA',
      celula: (v: VistoriaEnvioSga) => {
        const laudo = rotuloEnvioSga(v.addedToSga, 'Laudo');
        const fotos = rotuloEnvioSga(v.imagesAddedToSga, 'Fotos');
        return (
          <span className="tw-flex tw-flex-wrap tw-gap-1.5">
            <Chip variante={laudo.variante}>{laudo.texto}</Chip>
            <Chip variante={fotos.variante}>{fotos.texto}</Chip>
          </span>
        );
      },
    },
    {
      id: 'consultor',
      rotulo: 'Consultor',
      ocultarNoCard: true,
      celula: (v: VistoriaEnvioSga) => v.consultor || '—',
    },
    {
      id: 'cadastro',
      rotulo: 'Cadastrada em',
      ocultarNoCard: true,
      celula: (v: VistoriaEnvioSga) => formatarDataCurta(v.createdat),
    },
  ];

  const colunasDivergencias = [
    {
      id: 'registro',
      rotulo: 'Registro',
      principal: true,
      celula: (d: DivergenciaFila) => d.associadoNome ?? d.placa ?? '—',
    },
    {
      id: 'identificacao',
      rotulo: 'Identificação',
      secundaria: true,
      celula: (d: DivergenciaFila) =>
        d.entidade === 'VEICULO'
          ? d.placa ?? 'Veículo'
          : d.cpfCnpj
            ? formatCpfCnpj(d.cpfCnpj)
            : 'Associado',
    },
    {
      id: 'campo',
      rotulo: 'Campo',
      celula: (d: DivergenciaFila) => (
        <span className="tw-flex tw-flex-col tw-gap-0.5">
          <span className="tw-font-semibold tw-text-texto-corpo">{d.rotulo}</span>
          {/* Era `title` com a orientação — invisível no celular (at-54). */}
          {!d.aplicavel && (
            <span className="tw-text-xs tw-text-texto-suave">Exige ação manual</span>
          )}
        </span>
      ),
    },
    {
      id: 'valores',
      rotulo: 'Local × SGA',
      celula: (d: DivergenciaFila) => (
        <span className="[overflow-wrap:anywhere]">
          <span className="tw-text-texto-suave tw-line-through">{valorOuTraco(d.valorLocal)}</span>
          <span aria-hidden="true"> → </span>
          <span className="tw-font-semibold">{valorOuTraco(d.valorSga)}</span>
        </span>
      ),
    },
    {
      id: 'status',
      rotulo: 'Status',
      celula: (d: DivergenciaFila) => {
        const status = rotuloStatusDivergencia(d.status);
        return (
          <span className="tw-flex tw-flex-col tw-gap-0.5">
            <Chip variante={status.variante}>{status.texto}</Chip>
            {d.status !== 'ABERTA' && d.resolvidaEm && (
              <span className="tw-text-xs tw-text-texto-suave">
                em {formatarDataCurta(d.resolvidaEm)}
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: 'deteccao',
      rotulo: 'Detectada em',
      ocultarNoCard: true,
      celula: (d: DivergenciaFila) => (
        <span className="tw-text-xs tw-text-texto-suave">
          {formatarDataCurta(d.detectadaEm)}
          <br />
          {rotuloOrigemDivergencia(d.origemDeteccao)}
        </span>
      ),
    },
  ];

  /* ───────────────────────── painel ───────────────────────── */

  const totalItens =
    aba === 'divergencias'
      ? dadosDivergencias?.totalItems ?? 0
      : aba === 'associados'
        ? dados?.totalItems ?? 0
        : dadosVistorias?.totalItems ?? 0;

  const itensDaAba: unknown[] =
    aba === 'divergencias'
      ? dadosDivergencias?.items ?? []
      : aba === 'associados'
        ? dados?.items ?? []
        : dadosVistorias?.items ?? [];

  const vazio = () => {
    if (busca) {
      return (
        <EmptyState
          icone="busca"
          titulo="Nada encontrado para essa busca"
          descricao={`Nenhum registro desta aba corresponde a "${busca}".`}
          acao={
            <Botao variante="secundario" onClick={limparBusca}>
              Limpar a busca
            </Botao>
          }
        />
      );
    }

    if (filtrosAtivos > 0) {
      return (
        <EmptyState
          titulo="Nenhum registro com esses filtros"
          descricao="Nenhum registro desta aba casa com o recorte escolhido."
          acao={
            <Botao variante="secundario" onClick={limparFiltros}>
              Limpar os filtros
            </Botao>
          }
        />
      );
    }

    if (aba === 'divergencias') {
      return (
        <EmptyState
          titulo="Nenhuma divergência entre o cadastro local e o SGA"
          descricao="Quando a conferência encontrar uma diferença, ela aparece aqui com a orientação do caso."
        />
      );
    }

    return (
      <EmptyState
        icone="sem-dados"
        titulo={aba === 'associados' ? 'Nenhum associado cadastrado' : 'Nenhuma vistoria cadastrada'}
        descricao="Assim que houver cadastros, eles aparecem aqui com o estado do envio ao SGA."
      />
    );
  };

  const conteudo = () => {
    if (estado === 'carregando') return <Skeleton linhas={5} altura="tw-h-16" />;

    if (estado === 'erro') {
      return (
        <ErrorState
          mensagem="Não foi possível carregar o acompanhamento de envios."
          detalhe={erro}
          aoTentarNovamente={() => void carregar()}
        />
      );
    }

    if (itensDaAba.length === 0) return vazio();

    return (
      <>
        {/* at-76: resultado de "Verificar no SGA". Fica ACIMA da lista, e não dentro
            da linha, porque o componente já é a faixa usada nas telas de fluxo — e
            porque o TI acabou de pedir aquela conferência: o resultado não pode
            exigir rolagem para ser encontrado. Não bloqueia nada. */}
        {aba === 'associados' && associadoVerificadoId && divergenciasSga.length > 0 && (
          <div className="tw-mb-4">
            <AvisoDivergenciaSga divergencias={divergenciasSga} />
          </div>
        )}
        {aba === 'associados' && dados && (
          <DataList
            itens={dados.items}
            colunas={colunasAssociados}
            chave={(a) => a.id}
            rotuloAcessivel="Associados e veículos pendentes de envio ao SGA"
            acoes={(a) => (
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                {!a.addedToSga && (
                  <Botao
                    variante="primario"
                    tamanho="sm"
                    disabled={ocupado}
                    carregando={enviando === `associado:${a.id}`}
                    onClick={() => void enviarAssociado(a)}
                  >
                    Enviar associado
                  </Botao>
                )}
                <Botao
                  variante="secundario"
                  tamanho="sm"
                  disabled={ocupado || verificandoSgaId !== null}
                  carregando={verificandoSgaId === a.id}
                  onClick={() => void verificarNoSga(a)}
                >
                  Verificar no SGA
                </Botao>
                <Botao
                  variante="secundario"
                  tamanho="sm"
                  disabled={ocupado}
                  onClick={() => verEnvioAssociado(a)}
                >
                  Ver envio
                </Botao>
                <Botao
                  variante="fantasma"
                  tamanho="sm"
                  disabled={ocupado}
                  onClick={() => setDetalheAssociadoId(a.id)}
                >
                  Detalhes
                </Botao>
              </div>
            )}
          />
        )}

        {aba === 'vistorias' && dadosVistorias && (
          <DataList
            itens={dadosVistorias.items}
            colunas={colunasVistorias}
            chave={(v) => v.id}
            rotuloAcessivel="Vistorias pendentes de envio ao SGA"
            acoes={(v) => (
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                {!v.addedToSga && (
                  <Botao
                    variante="primario"
                    tamanho="sm"
                    disabled={ocupado}
                    carregando={enviando === `vistoria:${v.id}`}
                    onClick={() => void enviarVistoria(v)}
                  >
                    Enviar laudo
                  </Botao>
                )}
                {!v.imagesAddedToSga && (
                  <Botao
                    variante="primario"
                    tamanho="sm"
                    disabled={ocupado || !v.codigoVeiculo}
                    carregando={enviando === `fotos:${v.id}`}
                    onClick={() => void enviarFotos(v)}
                  >
                    Enviar fotos
                  </Botao>
                )}
                <Botao
                  variante="secundario"
                  tamanho="sm"
                  disabled={ocupado}
                  onClick={() => verEnvioVistoria(v)}
                >
                  Ver envio
                </Botao>
                <Botao
                  variante="fantasma"
                  tamanho="sm"
                  disabled={ocupado}
                  onClick={() => setDetalheVistoriaId(v.id)}
                >
                  Detalhes
                </Botao>
              </div>
            )}
          />
        )}

        {aba === 'divergencias' && dadosDivergencias && (
          <DataList
            itens={dadosDivergencias.items}
            colunas={colunasDivergencias}
            chave={(d) => d.id}
            rotuloAcessivel="Divergências entre o cadastro local e o SGA"
            acoes={(d) => (
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                {d.status === 'ABERTA' && (
                  <>
                    <Botao
                      variante="primario"
                      tamanho="sm"
                      disabled={ocupado || resolvendo}
                      onClick={() => resolver(d, 'aceitar')}
                    >
                      Aceitar do SGA
                    </Botao>
                    <Botao
                      variante="secundario"
                      tamanho="sm"
                      disabled={ocupado || resolvendo}
                      onClick={() => resolver(d, 'ignorar')}
                    >
                      Ignorar
                    </Botao>
                  </>
                )}
                <Botao
                  variante="fantasma"
                  tamanho="sm"
                  disabled={ocupado}
                  onClick={() => setDetalheDivergenciaId(d.id)}
                >
                  Detalhes
                </Botao>
              </div>
            )}
          />
        )}

        <div className="tw-mt-4">
          <Pagination
            pagina={page}
            totalItens={totalItens}
            porPagina={LIMIT}
            aoTrocar={setPage}
            ocupado={ocupado}
          />
        </div>
      </>
    );
  };

  return (
    <ShellSaac
      cabecalho={
        /* ⚠️ "(SGA)" saiu do título e foi para o sobretítulo: na faixa do mobile o
           título disputa espaço com o gatilho do menu, o voltar e o sino, e
           "Acompanhamento de Envios (SGA)" seria truncado (lição do "Acom..." da
           at-66). */
        <PageHeader
          titulo="Acompanhamento de Envios"
          sobretitulo="Administração (TI) · SGA"
          aoVoltar={voltar}
        />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-6xl md:tw-p-6">
          <div className="tw-bg-white tw-p-4 md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-p-6 md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-flex tw-flex-col tw-gap-4">
              <Nota>{NOTA_ENVIO_AUTOMATICO}</Nota>

              <Tabs
                idBase="envios"
                abas={ABAS}
                ativa={aba}
                aoTrocar={trocarAba}
                rotuloAcessivel="Seções do acompanhamento"
              />

              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                <SearchInput
                  key={chaveBusca}
                  valorInicial={busca}
                  placeholder={PLACEHOLDER_BUSCA[aba]}
                  rotuloAcessivel="Buscar nesta aba"
                  className="tw-min-w-0 tw-flex-1 tw-basis-64"
                  aoBuscar={trocarBusca}
                />

                <Botao
                  variante="secundario"
                  tamanho="md"
                  iconeEsquerda="filtro"
                  aria-pressed={filtrosAtivos > 0}
                  disabled={ocupado}
                  onClick={() => setFiltrosAberto(true)}
                >
                  {filtrosAtivos > 0 ? `Filtros (${filtrosAtivos})` : 'Filtros'}
                </Botao>
              </div>

              <Contadores
                aba={aba}
                dados={dados}
                dadosVistorias={dadosVistorias}
                dadosDivergencias={dadosDivergencias}
              />

              <div
                role="tabpanel"
                id={idDoPainel('envios')}
                aria-labelledby={idDaAba('envios', aba)}
                aria-busy={estado === 'carregando'}
              >
                {conteudo()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ModalDetalhesEnvioAssociado
        associado={detalheAssociado}
        ocupado={ocupado}
        enviando={enviando}
        onFechar={() => setDetalheAssociadoId(null)}
        onEnviarAssociado={(a) => void enviarAssociado(a)}
        onEnviarVeiculo={(placa, carroId) => void enviarVeiculo(placa, carroId)}
        onVerEnvioAssociado={verEnvioAssociado}
        onVerEnvioVeiculo={verEnvioVeiculo}
      />

      <ModalDetalhesEnvioVistoria
        vistoria={detalheVistoria}
        ocupado={ocupado}
        enviando={enviando}
        onFechar={() => setDetalheVistoriaId(null)}
        onEnviarVistoria={(v) => void enviarVistoria(v)}
        onEnviarFotos={(v) => void enviarFotos(v)}
        onVerEnvio={verEnvioVistoria}
      />

      <ModalDetalhesDivergencia
        divergencia={detalheDivergencia}
        ocupado={ocupado}
        resolvendo={resolvendo}
        onFechar={() => setDetalheDivergenciaId(null)}
        onAceitar={(d) => resolver(d, 'aceitar')}
        onIgnorar={(d) => resolver(d, 'ignorar')}
      />

      <ModalInformacoesEnviadas
        isOpen={infoEnviadas !== null}
        onClose={() => setInfoEnviadas(null)}
        filtro={infoEnviadas?.filtro ?? {}}
        titulo={infoEnviadas?.titulo}
      />

      {/* Textos preservados palavra por palavra (D6): é o que o TI leu e aprovou
          nas últimas branches, e a distinção entre aplicável e não aplicável é a
          regra do backend dita ao usuário. */}
      <ModalConfirmacao
        aberto={resolucao !== null}
        titulo={resolucao?.acao === 'ignorar' ? 'Ignorar divergência' : 'Aceitar dado do SGA'}
        mensagem={
          resolucao === null
            ? ''
            : resolucao.acao === 'ignorar'
              ? `Tirar da fila a divergência de ${resolucao.divergencia.rotulo}?`
              : resolucao.divergencia.aplicavel
                ? `Gravar "${valorOuTraco(resolucao.divergencia.valorSga)}" em ${
                    resolucao.divergencia.rotulo
                  } do cadastro local?`
                : `${resolucao.divergencia.rotulo} não pode ser aplicado automaticamente. Marcar como resolvida?`
        }
        detalhe={
          resolucao === null
            ? undefined
            : resolucao.acao === 'ignorar'
              ? 'O cadastro local fica como está. A divergência não volta a aparecer numa próxima verificação.'
              : resolucao.divergencia.aplicavel
                ? `O valor local ("${valorOuTraco(
                    resolucao.divergencia.valorLocal,
                  )}") será substituído. Nada é alterado no SGA.`
                : (resolucao.divergencia.orientacao ?? undefined)
        }
        rotuloConfirmar={resolucao?.acao === 'ignorar' ? 'Ignorar' : 'Aceitar'}
        destrutivo={resolucao?.acao === 'ignorar'}
        processando={resolvendo}
        onConfirmar={() => void confirmarResolucao()}
        onCancelar={() => {
          if (!resolvendo) setResolucao(null);
        }}
      />

      <ModalFiltrosEnvios
        aberto={filtrosAberto}
        valor={filtros}
        mostrarTipo={aba === 'associados'}
        mostrarSituacao={aba !== 'divergencias'}
        mostrarDivergencia={aba === 'divergencias'}
        onFechar={() => setFiltrosAberto(false)}
        onAplicar={(novos) => {
          setFiltros(novos);
          setPage(1);
          setFiltrosAberto(false);
        }}
      />
    </ShellSaac>
  );
};

/**
 * Estado do associado + resumo dos veículos dele, na coluna "Situação".
 *
 * 📌 É o resumo que o card do celular já mostrava. No desktop ele substitui as duas
 * bolinhas (uma na linha do titular, outra em cada sub-linha de veículo).
 */
const SituacaoDoAssociado: React.FC<{ associado: AssociadoEnvioSga }> = ({ associado }) => {
  const estado = rotuloEnvioRegistro(associado.addedToSga);
  const pendentes = associado.carros.filter((c) => !c.addedToSga).length;

  return (
    <span className="tw-flex tw-flex-wrap tw-gap-1.5">
      <Chip variante={estado.variante}>Associado: {estado.texto}</Chip>

      {associado.carros.length === 0 ? (
        <Chip variante="neutro">Sem veículo</Chip>
      ) : (
        <Chip variante={pendentes > 0 ? 'pendente' : 'ok'}>
          {pendentes > 0
            ? `${pendentes} de ${associado.carros.length} veículo(s) pendente(s)`
            : `${associado.carros.length} veículo(s) no SGA`}
        </Chip>
      )}
    </span>
  );
};

/**
 * Os tipos dos veículos do associado — só os que dizem algo.
 *
 * ⚠️ "Adesão" é o caso comum e fica de fora: um chip em toda linha vira ruído e
 * esconde justamente a substituição, que é o que o TI procura (regra da at-28, que
 * o card do celular já seguia).
 */
const TiposDosVeiculos: React.FC<{ associado: AssociadoEnvioSga }> = ({ associado }) => {
  const tipos = Array.from(new Set(associado.carros.map((c) => c.tipo))).filter(
    (t) => t !== 'ADESAO',
  );

  if (tipos.length === 0) return <span className="tw-text-texto-desabilitado">—</span>;

  return (
    <span className="tw-flex tw-flex-wrap tw-gap-1.5">
      {tipos.map((t) => {
        const rotulo = rotuloTipoRegistro(t);
        return (
          <Chip key={t} variante={rotulo.variante}>
            {rotulo.texto}
          </Chip>
        );
      })}
    </span>
  );
};

/**
 * Contadores da fila.
 *
 * ⚠️ **São GLOBAIS e não seguem a busca nem os filtros** (convenção at-10/at-28):
 * dão o panorama da fila, não o resultado do recorte. O aviso disso estava num
 * `title` — como rótulo visível ele "ocupava largura e empurrava o grupo para uma
 * segunda linha" na barra antiga; aqui ele tem linha própria, acima dos chips, no
 * molde de `usuarios.tsx`.
 */
const Contadores: React.FC<{
  aba: Aba;
  dados: EnviosSgaResponse | null;
  dadosVistorias: AcompanhamentoVistoriasResponse | null;
  dadosDivergencias: ListaDivergencias | null;
}> = ({ aba, dados, dadosVistorias, dadosDivergencias }) => {
  const chips = (() => {
    if (aba === 'associados' && dados) {
      return (
        <>
          <Chip variante="pendente">{dados.totais.associadosPendentes} associado(s) pendente(s)</Chip>
          <Chip variante="pendente">{dados.totais.veiculosPendentes} veículo(s) pendente(s)</Chip>
        </>
      );
    }
    if (aba === 'vistorias' && dadosVistorias) {
      return (
        <>
          <Chip variante="pendente">{dadosVistorias.totais.vistoriasPendentes} laudo(s) pendente(s)</Chip>
          <Chip variante="pendente">{dadosVistorias.totais.fotosPendentes} foto(s) pendente(s)</Chip>
        </>
      );
    }
    if (aba === 'divergencias' && dadosDivergencias) {
      return (
        <>
          <Chip variante="pendente">{dadosDivergencias.totais.abertas} aberta(s)</Chip>
          <Chip variante="ok">{dadosDivergencias.totais.aceitas} aceita(s)</Chip>
          <Chip variante="neutro">{dadosDivergencias.totais.ignoradas} ignorada(s)</Chip>
        </>
      );
    }
    return null;
  })();

  if (!chips) return null;

  return (
    <div>
      <p className="tw-m-0 tw-mb-1.5 tw-text-[11px] tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
        Totais da fila — não mudam com a busca nem com os filtros
      </p>
      <div className="tw-flex tw-flex-wrap tw-gap-1.5">{chips}</div>
    </div>
  );
};

export default EnviosSgaPage;
